import { Stack } from "@mantine/core"
import { MultiSelect, LabelWithTooltip, SectionHeader } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { CONTENT_FILTERING_FIELDS } from "@tfg/types/constants/configurations.constants"
import { CONFIGURATIONS_SETTINGS_SCOPE } from "@tfg/types/schemas/communication-studio/configurationParameters"
import { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { queryClient } from "~/backend/queryClient"
import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { useFindStandardsByClientId } from "~/gen/api/hooks/useFindStandardsByClientId"
import { getConfigurationsByScopeQueryKey } from "~/gen/api/hooks/useGetConfigurationsByScope"
import { useUpdateContentFiltering } from "~/gen/api/hooks/useUpdateContentFiltering"
import { useComponentCreation } from "~/pages/Brand/CommunicationStudio/contexts/componentCreation/useComponentCreation"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export type ContentFilteringFormProps = {
  configuration: ContentFilteringProps
  clientId: string
}
export const StandardsConfigurations = ({
  configuration,
  clientId,
}: ContentFilteringFormProps) => {
  const { productSheetGroupId } = useComponentCreation()
  const { t } = useTranslation("communicationStudio")
  const query = useFindStandardsByClientId(clientId, {
    query: {
      enabled: !!clientId,
    },
  })
  const { mutate: updateConfigurations } = useUpdateContentFiltering({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getConfigurationsByScopeQueryKey(productSheetGroupId, {
            scope: CONFIGURATIONS_SETTINGS_SCOPE.CONTENT_FILTERING,
          }),
        })
        showSuccessNotification({
          title: t(
            "tabs.content.contentFiltering.standardsToShow.success.title"
          ),
          message: t(
            "tabs.content.contentFiltering.standardsToShow.success.message"
          ),
        })
      },
      onError: (error: unknown) => {
        showAxiosError({
          error: error as Error,
          notificationTitle: t(
            "tabs.content.contentFiltering.standardsToShow.error.title"
          ),
          notificationMessage: t(
            "tabs.content.contentFiltering.standardsToShow.error.message"
          ),
        })
      },
    },
  })
  const standardsToBeShown =
    configuration[CONTENT_FILTERING_FIELDS.standardsToBeShown]
  return (
    <DataFetchWrapper query={query}>
      {({ data }) => (
        <Stack gap={1}>
          <SectionHeader
            order={6}
            title={t("tabs.content.contentFiltering.standardsToShow.title")}
            subtitle={t(
              "tabs.content.contentFiltering.standardsToShow.subtitle"
            )}
            mb="xs"
            gap={1}
            titleFw={600}
          />
          <MultiSelect
            label={
              <LabelWithTooltip
                label={t("tabs.content.contentFiltering.standardsToShow.label")}
                hoverHelperText={t(
                  "tabs.content.contentFiltering.standardsToShow.labelTooltip"
                )}
              />
            }
            labelProps={{
              w: "100%",
            }}
            size="xs"
            searchable
            placeholder={t(
              "tabs.content.contentFiltering.standardsToShow.placeholder"
            )}
            emptyMessage={t(
              "tabs.content.contentFiltering.standardsToShow.emptyMessage"
            )}
            value={standardsToBeShown.map((standard) => standard.toString())}
            data={data?.data.map((standard) => ({
              value: standard.id.toString(),
              label: standard.name,
            }))}
            miw={300}
            onChange={(value) => {
              updateConfigurations({
                productSheetGroupId,
                data: {
                  [CONTENT_FILTERING_FIELDS.standardsToBeShown]: value,
                },
              })
            }}
          />
        </Stack>
      )}
    </DataFetchWrapper>
  )
}
